import { useRef } from "react";
import { Toaster } from "sonner";
import Configurator from "@/components/configurator.tsx";
import { ResizerGroup, ResizerHandle, ResizerPanel, ResizerSeparator } from "@/components/ui/resizer.tsx";
import Spritesheet from "@/components/ui/spritesheet.tsx";
import Diagnostics from "@/features/diagnostics/components/diagnostics.tsx";
import HistoryState from "@/features/history-state/components/history-state.tsx";
import RegisterHotkeys from "@/features/hotkeys/components/register-hotkeys.tsx";
import OneTrustListener from "@/features/tracking/components/one-trust-listener.tsx";
import { trackEvent } from "@/features/tracking/lib/tracking.ts";
import useEffectOnce from "@/hooks/use-effect-once.ts";
import {
    type Locale,
    type Navigation,
    type Nullable,
    type SiteEnum,
    WebComponentMode,
    type WebComponentModeEnum,
} from "@/types";
import Styles from "./components/layout/styles.tsx";
import GlobalsProvider from "./components/providers/globals-provider.tsx";
import { Portal } from "./components/ui/portal.tsx";
import type Deltalight3dConfigurator from "./deltalight-3d-configurator.ts";
import ModelRenderer from "./features/model-renderer/components/model-renderer.tsx";
import SubFamiliesConfigProvider from "./features/sub-families/providers/sub-families-config-provider.tsx";
import type { ConfigurationTabEnum, ModelCode, SmartNumber } from "./features/sub-families/types";
import QueryClient from "./providers/query-client.tsx";
import WebComponentHost from "./providers/web-component-host.tsx";
import StoreProvider from "./redux/components/store-provider.tsx";
import { cn } from "./utils/style.ts";

type Props = {
    smartNumber: Nullable<SmartNumber>;
    userInput: Nullable<SmartNumber>;
    modelCode: Nullable<ModelCode>;
    configurationTab: Nullable<ConfigurationTabEnum>;
    mode: WebComponentModeEnum;
    host: Deltalight3dConfigurator;
    property: Nullable<string>;
    navigation: Nullable<Navigation>;
    site: SiteEnum;
    locale: Locale;
    apiTokenUrl: Nullable<string>;
};

export default function App({
    host,
    smartNumber,
    userInput,
    modelCode,
    configurationTab,
    mode,
    property,
    navigation,
    site,
    locale,
    apiTokenUrl,
}: Props) {
    const panelRef = useRef<HTMLDivElement>(null);

    useEffectOnce(() => {
        trackEvent("3d-configurator-start", { event: "configurator_start" });
    });

    return (
        <>
            <StoreProvider smartNumber={smartNumber}>
                <Styles />
                <Spritesheet />
                <RegisterHotkeys />
                <WebComponentHost host={host}>
                    <HistoryState />
                    <GlobalsProvider
                        mode={mode}
                        configurationTab={configurationTab}
                        property={property}
                        navigation={navigation}
                        site={site}
                        locale={locale}
                        apiTokenUrl={apiTokenUrl}
                    >
                        <QueryClient>
                            <SubFamiliesConfigProvider modelCode={modelCode} userInput={userInput}>
                                <ResizerGroup className={cn("app", mode !== WebComponentMode.Website && "app--portal")}>
                                    <ResizerPanel minSize="15%">
                                        <ModelRenderer />
                                    </ResizerPanel>
                                    <ResizerSeparator className="resizer__bar">
                                        <ResizerHandle />
                                    </ResizerSeparator>
                                    <ResizerPanel
                                        elementRef={panelRef}
                                        className="configuration-panel__bottom"
                                        minSize="35%"
                                    >
                                        <Configurator panelRef={panelRef} />
                                    </ResizerPanel>
                                    <Diagnostics />
                                </ResizerGroup>
                            </SubFamiliesConfigProvider>
                        </QueryClient>
                    </GlobalsProvider>
                </WebComponentHost>
            </StoreProvider>
            <Portal portalId="notifications">
                <Toaster position="top-right" richColors />
            </Portal>
            <OneTrustListener />
        </>
    );
}
